import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { MapPin, Navigation, Clock } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import API_URL from '../utils/api';

const createNumberedIcon = (num) => L.divIcon({
    html: `<div style="background:#2563eb;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${num}</div>`,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
});

const RouteMap = ({ employees = [] }) => {
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [routeData, setRouteData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchRoute = async () => {
        if (!selectedEmployee || !selectedDate) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/visits/route/${selectedEmployee}/${selectedDate}`, { withCredentials: true });
            setRouteData(data);
        } catch (error) {
            setRouteData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedEmployee && selectedDate) fetchRoute();
    }, [selectedEmployee, selectedDate]);

    const routePositions = routeData?.route?.map(r => [r.location.latitude, r.location.longitude]) || [];
    const center = routePositions.length > 0 ? routePositions[0] : [28.6139, 77.2090];

    return (
        <Card className="rounded-none border-slate-200" data-testid="route-map">
            <CardHeader className="border-b border-slate-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Navigation className="h-5 w-5 text-brand" />Route Tracking
                    </CardTitle>
                    <div className="flex gap-2">
                        <Select value={selectedEmployee || "none"} onValueChange={(v) => setSelectedEmployee(v === "none" ? "" : v)}>
                            <SelectTrigger className="w-[180px] rounded-none" data-testid="route-employee-select"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                            <SelectContent className="rounded-none">
                                <SelectItem value="none">Select Employee</SelectItem>
                                {employees.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-[150px] rounded-none" data-testid="route-date-input" />
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {!selectedEmployee ? (
                    <div className="py-12 text-center text-slate-500">Select an employee and date to view their route</div>
                ) : loading ? (
                    <div className="py-12 text-center text-slate-500">Loading route...</div>
                ) : (
                    <>
                        <div style={{ height: '400px' }}>
                            <MapContainer center={center} zoom={routePositions.length > 0 ? 13 : 10} style={{ height: '100%', width: '100%' }} key={`${selectedEmployee}-${selectedDate}`}>
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                                {routeData?.route?.map((stop, i) => (
                                    <Marker key={i} position={[stop.location.latitude, stop.location.longitude]} icon={createNumberedIcon(i + 1)}>
                                        <Popup>
                                            <div className="text-sm">
                                                <strong>{stop.customer_name}</strong><br />
                                                {stop.check_in_time && <span>In: {new Date(stop.check_in_time).toLocaleTimeString()}<br /></span>}
                                                {stop.check_out_time && <span>Out: {new Date(stop.check_out_time).toLocaleTimeString()}<br /></span>}
                                                {stop.duration_minutes > 0 && <span>Duration: {Math.round(stop.duration_minutes)} min<br /></span>}
                                                {stop.outcome && <span>Outcome: {stop.outcome}</span>}
                                            </div>
                                        </Popup>
                                    </Marker>
                                ))}
                                {routePositions.length > 1 && (
                                    <Polyline positions={routePositions} color="#2563eb" weight={3} dashArray="8,8" />
                                )}
                            </MapContainer>
                        </div>
                        {routeData && (
                            <div className="p-4 border-t border-slate-200 flex flex-wrap gap-6 text-sm">
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-brand" />
                                    <span><strong>{routeData.total_visits}</strong> visits</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Navigation className="h-4 w-4 text-green-600" />
                                    <span><strong>{routeData.total_distance_km}</strong> km traveled</span>
                                </div>
                                {routeData.route?.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-amber-600" />
                                        <span><strong>{Math.round(routeData.route.reduce((sum, r) => sum + (r.duration_minutes || 0), 0))}</strong> min total</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {routeData && routeData.total_visits === 0 && (
                            <div className="py-8 text-center text-slate-500">No visits with check-in data for this date</div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default RouteMap;
